/**
 * Sorting utilities for training sessions.
 *
 * These functions replicate the backend's COALESCE(start_date, created_at) logic,
 * ensuring that sessions with null start_date (which mean "starts immediately") are
 * sorted by their creation date instead of being clumped at the end of the list.
 */

export interface TrainingSession {
	id: string
	name: string
	start_date: string | null
	created_at: string
	[key: string]: unknown
}

/**
 * Sorts training sessions by start_date (or created_at if start_date is null).
 * Replicates the backend's COALESCE(start_date, created_at) logic.
 *
 * @param sessions - Array of training sessions to sort
 * @param direction - 'asc' for ascending, 'desc' for descending (default: 'desc')
 * @returns Sorted array (does not mutate the original)
 *
 * @example
 * const sorted = sortTrainingSessions(sessions, 'desc')
 * // Sessions are ordered by start_date DESC, with null start_date sessions
 * // spliced in by their created_at value
 */
export function sortTrainingSessions(
	sessions: TrainingSession[],
	direction: 'asc' | 'desc' = 'desc'
): TrainingSession[] {
	return [...sessions].sort((a, b) => {
		// Use start_date if available, otherwise fall back to created_at
		// (replicates COALESCE(start_date, created_at) from backend)
		const dateA = a.start_date ? new Date(a.start_date).getTime() : new Date(a.created_at).getTime()
		const dateB = b.start_date ? new Date(b.start_date).getTime() : new Date(b.created_at).getTime()

		if (direction === 'desc') {
			return dateB - dateA
		} else {
			return dateA - dateB
		}
	})
}
